import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import config from '../../config';
import { bareEndpointPath, endpointInvokeUrl, type EndpointUrlParts } from '@/utils/endpointUrl';
import {
  BRIDGE_REQUEST_TYPE,
  buildInvokeBridgeShim,
  handleBridgeRequest,
} from './previewInvokeBridge';

interface PreviewSandboxProps {
  /** The generated front-end source to render. */
  source: string | null;
  /**
   * The endpoint this generated page invokes, when it is known. Supplies
   * `window.__INVOKE_URL__` (see `previewInvokeUrl` below); omitting it leaves
   * the page on the URL baked into its own source.
   */
  endpoint?: EndpointUrlParts | null;
  className?: string;
}

/**
 * Renders generated source inside a hardened, isolated iframe.
 *
 * Isolation is load-bearing: the `sandbox` attribute deliberately grants
 * `allow-scripts` but NOT `allow-same-origin`, so the frame runs in an opaque
 * (null) origin. Generated code therefore cannot reach the Composer shell — it
 * has no access to the parent DOM, cookies, `localStorage`, or the Keycloak
 * session — while still being able to make network calls to the endpoint (CORS
 * on the server, governed by the endpoint's auth-policy origins, is the gate).
 *
 * The source is delivered via `srcDoc` (never injected into the parent DOM). A
 * full HTML document is used as-is; a bare fragment/script is wrapped in a
 * minimal shell. The endpoint's API base URL is exposed as `window.__API_URL__`
 * so generated code has a single place to read it from, and the endpoint's own
 * invoke URL as `window.__INVOKE_URL__` (task 938).
 *
 * Sizing (task 397): the frame grows to fit the whole generated interface
 * instead of scrolling inside a fixed window. Because the frame is opaque, the
 * parent cannot read `contentDocument.scrollHeight` — that throws. So the
 * injected bootstrap measures the document from the inside and posts its height
 * out over `postMessage`; the parent authenticates the sender by object
 * identity and applies the height. The height channel is the *reason* the
 * isolation can stay intact: do not add `allow-same-origin` to make measuring
 * easier, it would hand generated code same-origin access to the app.
 *
 * Running the AI Service (task 1071): the opaque frame holds no session, so a
 * page calling `__INVOKE_URL__` itself reached innovate-service anonymously, was
 * classed an outside-the-app (`billable`) call, and was refused by the
 * `api_access` gate on the Free plan. The bootstrap therefore also installs a
 * `fetch` shim that relays API-host requests to this component over the same
 * postMessage channel, and this component performs them as the signed-in owner
 * (see `previewInvokeBridge.ts`). Like the height channel, the bridge is what
 * lets the isolation stay intact: the frame still cannot read the token or
 * anything else of the app's — it can only ask for the previewed endpoint's
 * invoke URL or its polling handle, and the parent decides.
 */

/** Below this a short UI would render as a sliver; above it, the frame scrolls. */
export const MIN_PREVIEW_HEIGHT = 240;
/**
 * Ceiling for a reported height. A generated page with a runaway or endlessly
 * growing layout cannot push the iframe past this; past it the frame keeps its
 * own inner scrollbar (the old behavior, now only in the pathological case).
 */
export const MAX_PREVIEW_HEIGHT = 4000;
/** Height held before the first measurement lands (or if none ever does). */
export const DEFAULT_PREVIEW_HEIGHT = 540;

/** Identifies our own height messages among whatever else posts to the window. */
const PREVIEW_MESSAGE_SOURCE = 'innovate-preview';
/** Sub-pixel churn must not drive a measure → resize → measure loop. */
const HEIGHT_EPSILON_PX = 2;

/**
 * The whole URL this generated page invokes, or null when it cannot be known.
 *
 * Generated pages resolve their target as
 * `window.__INVOKE_URL__ || "<baked absolute platform URL>"` **verbatim** (task
 * 938) — they never compose `/api/<slug>/<path>` onto an injected base
 * themselves, because the shape of that path differs between the platform host
 * and a custom domain, and a page copied out of here can be served by either.
 * So what goes into the seam is the complete URL, never a prefix.
 *
 * Null whenever the endpoint's identity is not settled — no endpoint in hand,
 * no account slug (a legacy or not-yet-claimed row), or an empty path (an
 * endpoint being composed before it is saved). A partial URL would be worse
 * than none: the `||` fallback would not fire, and the page would call a
 * truncated address instead of the absolute platform URL baked into its source.
 */
function previewInvokeUrl(endpoint: EndpointUrlParts | null | undefined): string | null {
  if (!endpoint || !endpoint.accountSlug || !bareEndpointPath(endpoint.path)) return null;
  return endpointInvokeUrl(endpoint);
}

/**
 * Bootstrap injected into the frame: the API base URL, the endpoint's own
 * invoke URL when it is known, plus a height reporter that measures from the
 * inside (the only side that can) and posts the result to the parent on load
 * and on every subsequent content resize.
 *
 * `__API_URL__` is unchanged — it stays the prefix a page appends a polling
 * path to — and `__INVOKE_URL__` is added beside it rather than replacing it.
 * The assignment is emitted only when there is a complete URL to assign, so an
 * unknown endpoint leaves the property undefined and the page's own fallback
 * takes over.
 */
function buildBootstrap(invokeUrl: string | null): string {
  // The invoke bridge is keyed to the previewed endpoint: with no invoke URL the
  // parent has nothing it would relay, so the frame gets no shim either.
  const bridge = invokeUrl ? buildInvokeBridgeShim() : '';
  const payload = JSON.stringify({
    apiUrl: config.API_URL,
    ...(invokeUrl ? { invokeUrl } : {}),
    messageSource: PREVIEW_MESSAGE_SOURCE,
  });

  return `<script>(function(){
  var boot = ${payload};
  window.__API_URL__ = boot.apiUrl;${invokeUrl ? '\n  window.__INVOKE_URL__ = boot.invokeUrl;' : ''}${bridge}
  var last = 0;
  function report() {
    var doc = document.documentElement;
    if (!doc) return;
    var height = Math.max(
      doc.scrollHeight,
      doc.offsetHeight,
      document.body ? document.body.scrollHeight : 0,
      document.body ? document.body.offsetHeight : 0
    );
    if (!height || Math.abs(height - last) < ${HEIGHT_EPSILON_PX}) return;
    last = height;
    try {
      window.parent.postMessage(
        { source: boot.messageSource, type: 'height', height: height },
        '*'
      );
    } catch (e) { /* parent went away — nothing to report to */ }
  }
  window.addEventListener('load', report);
  document.addEventListener('DOMContentLoaded', report);
  if (typeof ResizeObserver === 'function') {
    try {
      new ResizeObserver(report).observe(document.documentElement);
    } catch (e) { /* observer unavailable — load/DOMContentLoaded still report */ }
  }
  report();
})();</script>`;
}

/**
 * Would this source paint anything at all? (task 876)
 *
 * `!source` used to be the only guard in this file, so every other string went
 * to `srcDoc` and whatever it painted was what the User saw. Two shapes paint
 * NOTHING — a whitespace-only source, and a well-formed document whose `<body>`
 * is empty — and both present as a pure white iframe with nothing on the page
 * explaining it, under a header that says the version saved fine. That is the
 * one mechanism in this component that produces exactly the reported symptom.
 *
 * Deliberately STRING-LEVEL. Parsing untrusted generated markup into the app's
 * own document — `DOMParser`, an offscreen element, anything — would defeat the
 * point of the sandbox below, which exists so this code never touches that
 * markup as DOM. The two shapes above are recognizable without it.
 *
 * A source with no `<body>` element is left alone: a bare fragment (including a
 * script-only one, which the shell exists to wrap) is content, and generated
 * code may well build its DOM at runtime.
 */
export function isContentlessSource(source: string | null | undefined): boolean {
  if (!source || source.trim().length === 0) return true;

  const body = /<body[^>]*>([\s\S]*)<\/body>/i.exec(source);
  if (!body) return false;

  // Comments are not content; whitespace between them is not either.
  return body[1].replace(/<!--[\s\S]*?-->/g, '').trim().length === 0;
}

function buildSrcDoc(source: string, invokeUrl: string | null): string {
  const looksLikeDocument = /<!doctype html|<html[\s>]/i.test(source);
  const bootstrap = buildBootstrap(invokeUrl);

  if (looksLikeDocument) {
    // Inject the bootstrap right after <head> when present, else prepend.
    if (/<head[\s>]/i.test(source)) {
      return source.replace(/<head[^>]*>/i, (m) => `${m}${bootstrap}`);
    }
    return `${bootstrap}${source}`;
  }

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    ${bootstrap}
  </head>
  <body>
    ${source}
  </body>
</html>`;
}

/** Reported heights are clamped so neither extreme can break the page. */
function clampHeight(height: number): number {
  return Math.min(Math.max(height, MIN_PREVIEW_HEIGHT), MAX_PREVIEW_HEIGHT);
}

export default function PreviewSandbox({
  source,
  endpoint,
  className = '',
}: PreviewSandboxProps) {
  const contentless = useMemo(() => isContentlessSource(source), [source]);
  const invokeUrl = useMemo(() => previewInvokeUrl(endpoint), [endpoint]);
  const srcDoc = useMemo(
    () => (source && !contentless ? buildSrcDoc(source, invokeUrl) : ''),
    [source, contentless, invokeUrl]
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(DEFAULT_PREVIEW_HEIGHT);

  // A new source is a new document: drop back to the default so a tall UI does
  // not leave a tall empty box behind a short one until the first measurement.
  useEffect(() => {
    setHeight(DEFAULT_PREVIEW_HEIGHT);
  }, [srcDoc]);

  const handleMessage = useCallback((event: MessageEvent) => {
    // Authenticate the sender by object identity, NOT by origin. The frame is
    // sandboxed without `allow-same-origin`, so it posts from an opaque origin
    // and `event.origin` is the string "null" — it identifies nothing and must
    // never be used as the check here. Only the window we rendered counts.
    const data = event.data as { source?: unknown; type?: unknown; height?: unknown } | null;
    const isBridgeRequest =
      !!data && data.source === PREVIEW_MESSAGE_SOURCE && data.type === BRIDGE_REQUEST_TYPE;

    const frame = iframeRef.current;
    if (!frame || !event.source || event.source !== frame.contentWindow) {
      // A bridge request from any window but our own frame is never relayed.
      if (isBridgeRequest) {
        console.warn('PreviewSandbox: ignored a preview bridge request from another window');
      }
      return;
    }

    // An invoke relayed by the frame's fetch shim (task 1071). The sender check
    // above is the same one the height channel uses — object identity, never origin.
    if (isBridgeRequest) {
      void handleBridgeRequest(data, frame.contentWindow as Window, invokeUrl, PREVIEW_MESSAGE_SOURCE);
      return;
    }

    if (
      !data ||
      data.source !== PREVIEW_MESSAGE_SOURCE ||
      data.type !== 'height' ||
      typeof data.height !== 'number' ||
      !Number.isFinite(data.height)
    ) {
      return;
    }

    const next = clampHeight(data.height);
    setHeight((current) => (Math.abs(current - next) < HEIGHT_EPSILON_PX ? current : next));
  }, [invokeUrl]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  if (!source) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-sm text-gray-500 dark:text-gray-400 ${className}`}
        style={{ height: MIN_PREVIEW_HEIGHT }}
        data-testid="preview-empty"
      >
        Generate a UI to see a live preview here.
      </div>
    );
  }

  // A saved version that renders nothing says so (task 876). Its own state, and
  // its own testid: "nothing has been generated yet" (above) and "this version
  // is empty" are different facts about the service, and only the second one
  // means something went wrong upstream.
  if (contentless) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-amber-300 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-900/20 px-6 text-center text-sm text-amber-800 dark:text-amber-200 ${className}`}
        style={{ height: MIN_PREVIEW_HEIGHT }}
        data-testid="preview-no-content"
      >
        <span className="font-medium">This version has no content to show</span>
        <span className="text-xs text-amber-700 dark:text-amber-300/80">
          It was saved with an empty page. Select another version, or ask for the interface again.
        </span>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      data-testid="preview-sandbox"
      title="Generated UI preview"
      srcDoc={srcDoc}
      // No allow-same-origin: the frame is a null origin and cannot reach the shell.
      // Running the AI Service as the owner goes through the invoke bridge
      // (previewInvokeBridge.ts), which relays only the previewed endpoint's
      // invoke and polling calls — adding allow-same-origin instead would hand
      // generated code the app's session and DOM. Keep it off.
      sandbox="allow-scripts allow-forms allow-modals allow-popups"
      // Height follows the generated content (reported over postMessage), so the
      // interface is seen whole rather than through a fixed window.
      style={{ height }}
      className={`w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white ${className}`}
    />
  );
}
